import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { IBelugaVisitSummary } from '@care-portals/api-interfaces';
import { APIService } from '../../../services/api/api.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'portals-emr-order-visit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzSpinModule,
    NzTagModule,
    NzModalModule,
    NzToolTipModule
  ],
  templateUrl: './order-visit.component.html',
  styleUrls: ['./order-visit.component.less']
})
export class OrderVisitComponent implements OnChanges, OnDestroy {
  @Input() orderId: number;
  @Input() masterId: string;
  @Output() visitCanceled = new EventEmitter<void>();

  visit: IBelugaVisitSummary | null = null;
  isLoadingVisit = false;
  isCancelingVisit = false;
  visitError: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly apiService: APIService,
    private readonly modalService: NzModalService,
    private readonly notification: NzNotificationService,
    private readonly clipboard: Clipboard
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['orderId'] || changes['masterId']) &&
      this.orderId &&
      this.masterId
    ) {
      this.loadVisit();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get submittedMedIdsLabel(): string {
    const medIds = this.visit?.submittedMedIds?.filter(Boolean) || [];
    return medIds.length ? Array.from(new Set(medIds)).join(', ') : '—';
  }

  get visitStatusColor(): string {
    switch (this.visit?.visitStatus) {
      case 'active':
        return 'blue';
      case 'resolved':
        return 'green';
      case 'canceled':
        return 'red';
      case 'holding':
      case 'pending':
        return 'orange';
      case 'admin':
        return 'purple';
      default:
        return 'default';
    }
  }

  copyMasterId(): void {
    const value = this.visit?.masterId || this.masterId;
    if (!value) {
      return;
    }
    this.clipboard.copy(value);
    this.notification.success('Copied to clipboard', '');
  }

  loadVisit(): void {
    if (!this.orderId || !this.masterId) {
      return;
    }

    this.isLoadingVisit = true;
    this.visitError = null;
    this.apiService
      .getOrderVisit(this.orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: visit => {
          this.visit = visit;
          this.isLoadingVisit = false;
        },
        error: error => {
          this.visit = null;
          this.visitError =
            error?.error?.message || error?.message || 'Failed to load visit';
          this.isLoadingVisit = false;
        }
      });
  }

  confirmCancelVisit(): void {
    this.modalService.confirm({
      nzTitle: 'Cancel Beluga visit?',
      nzContent:
        'This will cancel the visit in Beluga and remove the master ID from this order.',
      nzOkText: 'Cancel Visit',
      nzOkDanger: true,
      nzCancelText: 'Keep Visit',
      nzOnOk: () => this.cancelVisit()
    });
  }

  private cancelVisit(): Promise<boolean> {
    this.isCancelingVisit = true;
    return new Promise(resolve => {
      this.apiService
        .cancelOrderVisit(this.orderId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isCancelingVisit = false;
            this.notification.success(
              'Visit canceled',
              'The Beluga visit was canceled and master ID was removed.'
            );
            this.visitCanceled.emit();
            resolve(true);
          },
          error: error => {
            this.isCancelingVisit = false;
            this.notification.error(
              'Cancel failed',
              error?.error?.message || error?.message || 'Unknown error'
            );
            resolve(false);
          }
        });
    });
  }
}
